import { format } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { safeGetItem, safeSetItem } from '@/lib/safe-storage';

// Common timezones
export const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST, no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris/Berlin (CET)' },
  { value: 'Europe/Athens', label: 'Athens (EET)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZDT/NZST)' },
];

// Get timezone abbreviation (e.g., "EST", "CST", "PST")
export function getTimezoneAbbr(timezone: string): string {
  try {
    const now = new Date();
    const formatted = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    }).formatToParts(now);
    
    const tzPart = formatted.find(part => part.type === 'timeZoneName');
    return tzPart?.value || timezone;
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error);
    return timezone;
  }
}

// Convert a time string (HH:mm) on a specific date to user's selected timezone
export function convertTimeToTimezone(
  date: Date,
  timeString: string,
  fromTimezone: string,
  toTimezone: string
): string {
  try {
    const [hours, minutes] = timeString.split(':').map(Number);
    
    // Create a date in the source timezone
    const sourceDate = new Date(date);
    sourceDate.setHours(hours, minutes, 0, 0);
    
    // Convert from source timezone to UTC, then to target timezone
    const utcDate = fromZonedTime(sourceDate, fromTimezone);
    const targetDate = toZonedTime(utcDate, toTimezone);
    
    // Format as HH:mm
    return format(targetDate, 'HH:mm');
  } catch (error) {
    console.error('Error converting timezone:', error);
    return timeString;
  }
}

// Get the user's detected timezone
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// Get stored timezone preference or default to user's timezone
export function getStoredTimezone(): string {
  const aliasMap: Record<string, string> = {
    'US/Pacific': 'America/Los_Angeles',
    'US/Mountain': 'America/Denver',
    'US/Central': 'America/Chicago',
    'US/Eastern': 'America/New_York',
    'US/Arizona': 'America/Phoenix',
    'US/Alaska': 'America/Anchorage',
    'US/Hawaii': 'Pacific/Honolulu'
  };

  const normalize = (tz: string | null) => {
    if (!tz) return null;
    return aliasMap[tz] || tz;
  };

  const storedRaw = safeGetItem('preferredTimezone');
  const stored = normalize(storedRaw);
  if (stored) return stored;
  
  const userTzRaw = getUserTimezone();
  const userTz = normalize(userTzRaw) as string;

  // If it's in our list, return it; otherwise return the resolved timezone anyway
  return TIMEZONES.some(tz => tz.value === userTz) ? userTz : userTz;
}

// Save timezone preference
export function saveTimezonePreference(timezone: string): void {
  safeSetItem('preferredTimezone', timezone);
}
